<script setup lang="ts">
import type { Tables } from '@/types/database.types.ts';
import uniqBy from 'lodash.uniqby';
import {
  defineOgImageComponent,
  useAsyncData,
  useHead,
  useI18n,
  useRoute,
  useSupabaseClient,
  useToast,
} from '#imports';

const { showError } = useToast();
const { t } = useI18n();

const route = useRoute();
const supabase = useSupabaseClient();
const { data: destination, pending: destinationPending } = await useAsyncData(
  'destination:{id}',
  async (): Promise<Tables<'cities'>> => {
    const { data, error } = await supabase
      .from('cities')
      .select('*')
      .eq('id', route.params.id)
      .single();
    if (error) showError('Could not fetch destination');
    return data;
  },
);

const { data: maps } = await useAsyncData('destination_maps:{id}', async () => {
  const { data, error } = await supabase
    .from('spots')
    .select(`
      *,
      map_slug:map_id!inner(
        current_slug
      ),
      categories:category_id (
        id,
        name,
        icon
      ),
      cities:cities_id (
        id,
        name
      ),
      maps:map_id (
        *,
        profiles:user_id (
          id,
          profile_image_url,
          mappr_handle
        )
      )
    `)
    .eq('cities_id', route.params.id)
    .eq('is_published', true)
    .order('created_at', { ascending: false });
  if (error) showError(t('messages.maps.error-loading'));
  const spotMaps = data.map((spot) => spot.maps);
  const maps = uniqBy(spotMaps, 'id');
  const uniqueMaps = maps.filter((map) => map.is_published);

  // Group spots by category
  const spotsByCategory = data.reduce((acc, spot) => {
    if (!spot.categories) return acc;

    const categoryId = spot.categories.id;
    if (!acc[categoryId]) {
      acc[categoryId] = {
        id: categoryId,
        name: spot.categories.name,
        icon: spot.categories.icon,
        spots: []
      };
    }

    acc[categoryId].spots.push(spot);
    return acc;
  }, {});

  // Filter categories to only include those with 2 or more spots
  const categoriesWithMultipleSpots = Object.values(spotsByCategory).filter(
    category => category.spots.length >= 2
  );

  return {
    uniqueMaps,
    spots: data,
    categoriesWithMultipleSpots
  };
});

useHead(() => ({
  title: `${destination.value.name} - ${destination.value.country}`,
  meta: [
    {
      name: 'description',
      content: 'Explore the world and discover new places to visit',
    },
  ],
}));

defineOgImageComponent('Default', {
  title: `${destination.value.name} - ${destination.value.country}`,
  description: destination.value.summary,
  imagePath: destination.value.cover_image_url,
});
</script>

<template>
  <div>
    <div v-if="destinationPending">
      <MPLoader label="Loading destination" />
    </div>
    <div v-if="destination && !destinationPending" class="destination-page">
      <MPSectionHeader
        :title="destination.name"
        :subtitle="destination.country"
      />
      <div v-if="maps.categoriesWithMultipleSpots?.length > 2" class="categories-list">
        <MPCategoryList :categories="maps.categoriesWithMultipleSpots" />
      </div>
      <div class="destination-header">
        <MPImg :src="destination.cover_image_url" class="destination-image" :width="1500"/>
      </div><br>
      <h3>Maps</h3>
      <p class="summary">Curated maps for {{ destination.name }}</p>

      <MPViewportSpill show-controls class="maps">
        <LazyMPMapCard v-for="map in maps?.uniqueMaps" :key="map.id" :map="map" hide-details/>
      </MPViewportSpill>

      <h3>Spots</h3>
      <p class="summary">Discover unique spots in {{ destination.name }}</p>


      <MPViewportSpill show-controls class="spots">
        <LazyMPSpotCard v-for="spot in maps?.spots" :key="spot.id" :spot="spot" actions/>
      </MPViewportSpill>

      <div v-if="maps?.categoriesWithMultipleSpots?.length > 0">
          <div v-for="category in maps.categoriesWithMultipleSpots" :key="category.id" class="category-section">
            <h3>{{ category.name }}</h3>
            <p class="summary">{{ category.name }} spots in {{ destination.name }}</p>
            <MPViewportSpill show-controls class="spots">
              <LazyMPSpotCard v-for="spot in category.spots" :key="spot.id" :spot="spot" actions/>
            </MPViewportSpill>
          </div>
        </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import '@/assets/css/typography.pcss';
@import '@/assets/css/layouts.pcss';
@import '@/assets/css/breakpoints.pcss';
$gridGap:1rem;
.destination-page {
  @mixin layout-wrapper-wide;
  margin: 1.5rem auto;
  padding: 1rem;
  @mixin screen sm {
    padding: 2rem 1rem;
  }
}

.destination-header {
  width: 100%;
  position: relative;
  img {
    aspect-ratio: 16 / 7;
    object-fit: cover;
    width: 100%;
    border-radius: 1rem;
  }
}

.categories-list {
  margin: 0 auto 2rem;
  width: fit-content;
}

h3 {
  margin-bottom: 0;
}

p.summary {
  color: var(--color-slate-400);
  @mixin typeface-regular;
  margin-bottom: 1.5rem;
}

.destination-info {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100% - 3px);
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 1rem;
  justify-content: end;
  padding: 1rem 2rem;
  background: linear-gradient(to top right, rgba(0, 0, 0, 0.5), transparent);
  @mixin screen md {
    padding: 2rem 2rem;
  }
  h1 {
    @mixin typeface-title;
    color: var(--color-white);
    margin: 0.5rem 0 0 0;
  }
  p {
    color: var(--color-rose-400);
    margin: 0;
  }
}


.maps-list + h3 {
  margin-top: 4rem;
}

.maps {
  width: 100%;
  gap: $gridGap;
  &:deep(.map-card) {
    width: calc(($layoutWide / 5) - $gridGap);
  }
}
.spots {
  width: 100%;
  gap: $gridGap;
  &:deep(.spot) {
    width: calc(($layoutWide / 5) - $gridGap);
  }
}

</style>
